import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Bell,
  Calendar,
  CalendarClock,
  CreditCard,
  Ticket,
  HandPlatter,
  ShieldCheck,
  Sparkles,
  ShoppingCart,
  Store,
  User,
  Wallet,
  X,
  PlaySquare,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading.tsx";
import { Socio } from "../types/Socio.tsx";
import { useAuth } from "../context/AuthProvider.tsx";
import DeudaInfo from "./DeudaInfo.tsx";
import { canjearCuponDescuento, obtenerCuponesPorSocio } from "../services/cuponService.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import socioService from "../services/socioService.tsx";
import { getPuntosBySocio, type PuntoItem } from "../services/puntosService.tsx";
import { getInicioResumenSocio } from "../services/dashboardService.ts";
import { getSocioPagosConSos } from "../services/cuotasService.tsx";
import { getRubrosNombres } from "../enums/Rubro.tsx";

interface DashboardProps {
  socio?: Socio;
  isAdminView?: boolean;
}

type QuickAction = {
  title: string;
  description: string;
  link: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  accent: string;
};

type NivelMembresia = { nombre: "Bronce" | "Plata" | "Oro"; min: number; next: number | null };
const getNivel = (puntos: number): NivelMembresia => {
  if (puntos >= 5000) return { nombre: "Oro", min: 5000, next: null };
  if (puntos >= 1500) return { nombre: "Plata", min: 1500, next: 5000 };
  return { nombre: "Bronce", min: 0, next: 1500 };
};

const InfoBadge = ({ label, value }: { label: string; value: string | number }) => (
  <div className="rounded-lg bg-white/15 px-3 py-2 text-xs font-semibold text-white shadow-sm backdrop-blur">
    <p className="uppercase tracking-wide text-white/80">{label}</p>
    <p className="text-sm">{value}</p>
  </div>
);

const getSosFechaHasta = () => `${new Date().getFullYear()}-12-31`;

const calcularSaldoMovimientosSos = (movimientos: any[]) => {
  const total = (movimientos || []).reduce((acc, m) => {
    const debe = Number(m?.montodebe || 0);
    const haber = Number(m?.montohaber || 0);
    return acc + debe - haber;
  }, 0);

  return Number(total.toFixed(2));
};

export default function Dashboard({ socio: propSocio }: DashboardProps) {
  const [socio, setSocio] = useState<Socio>();
  const [deudaTotal, setDeudaTotal] = useState<number | null>(null);
  const [cuponesAsignados, setCuponesAsignados] = useState<number | null>(null);
  const [puntosTotal, setPuntosTotal] = useState<number>(0);
  const [historialPuntos, setHistorialPuntos] = useState<PuntoItem[]>([]);
  const [misCupones, setMisCupones] = useState<any[]>([]);
  const [canjeando, setCanjeando] = useState(false);
  const [canjeMensaje, setCanjeMensaje] = useState<string>("");
  const [showPuntosModal, setShowPuntosModal] = useState(false);
  const [loadingHistorialModal, setLoadingHistorialModal] = useState(false);
  const [pagePuntos, setPagePuntos] = useState(1);
  const [loading, setLoading] = useState<boolean>(true);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const domicilioElectronico = socio?.socio_mail || "—";
  const logoSocio = (socio as any)?.logoUrl || socio?.socio_firma || "";
  const rubroNombre =
    getRubrosNombres(
      socio?.socio_rubro ? Number(socio.socio_rubro) : null,
      socio?.socio_rubros
    ) || "No informado";
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [logoSocio]);

  useEffect(() => {
    const refreshSocio = async () => {
      if (!socio?.socio_id) return;
      try {
        const fresh = await socioService.getSocioById(Number(socio.socio_id), getAccessTokenSilently);
        setSocio((prev) => ({ ...(prev || {}), ...fresh } as Socio));
        socioService.saveSelectedSocioInLocalStorage({ ...(socio || {}), ...fresh } as Socio);
      } catch (e) {
        console.warn("No se pudo refrescar socio para logo", e);
      }
    };

    refreshSocio();
  }, [socio?.socio_id, getAccessTokenSilently]);

  const cargarSocio = () => {
    if (propSocio) {
      setSocio(propSocio);
      return;
    }
    const storedSocio = localStorage.getItem("socioSeleccionado");
    if (storedSocio) {
      try {
        const socioParsed = JSON.parse(storedSocio) as Socio;
        setSocio(socioParsed);
      } catch (error) {
        console.error("Error al cargar el socio:", error);
      }
    }
  };

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      const stored = localStorage.getItem("socioSeleccionado");
      if (!propSocio && !stored && isAdmin) {
        navigate("/admin", { replace: true });
        return;
      }
      cargarSocio();
      try {
        if (stored) {
          const socioParsed = JSON.parse(stored) as Socio;
          if (socioParsed.socio_id) {
            const token = await getAccessTokenSilently();

            try {
              const resumen = await getInicioResumenSocio(socioParsed.socio_id, token);
              setDeudaTotal(Number(resumen?.deudaTotal || 0));
              setPuntosTotal(Number(resumen?.puntosTotal || 0));
              setCuponesAsignados(Number(resumen?.cuponesAsignados || 0));
              setMisCupones(Array.isArray(resumen?.misCupones) ? resumen.misCupones : []);
            } catch (err) {
              console.warn("No se pudo obtener resumen agregado del inicio", err);
            }

            try {
              const detalleCuenta = await getSocioPagosConSos(socioParsed.socio_id, getAccessTokenSilently, {
                includeSosMovimientos: true,
                refreshSos: true,
                fechaDesde: "2024-01-01",
                fechaHasta: getSosFechaHasta(),
              });
              const movimientosSos = Array.isArray(detalleCuenta?.movimientos_sos)
                ? detalleCuenta.movimientos_sos
                : [];

              if (movimientosSos.length) {
                setDeudaTotal(calcularSaldoMovimientosSos(movimientosSos));
              }
            } catch (err) {
              console.warn("No se pudo obtener cuenta SOS para inicio", err);
            }

            try {
              const puntosResp = await getPuntosBySocio(socioParsed.socio_id, token);
              setHistorialPuntos(Array.isArray(puntosResp?.items) ? puntosResp.items.slice(0, 20) : []);
            } catch (err) {
              console.warn("No se pudieron obtener movimientos de puntos del socio", err);
            }

          }
        }
      } catch (err) {
        console.error("No se pudo obtener deuda total", err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [propSocio, isAdmin, navigate]);

  const cuponesDescription =
    cuponesAsignados === null
      ? "Revisá tus cupones y beneficios disponibles."
      : cuponesAsignados === 0
      ? "No tenés cupones asignados aún."
      : `${cuponesAsignados} cupón(es) asignados a tu cuenta.`;

  const handleCanjear5 = async () => {
    const stored = localStorage.getItem("socioSeleccionado");
    if (!stored) return;

    try {
      setCanjeando(true);
      setCanjeMensaje("");
      const socioParsed = JSON.parse(stored) as Socio;
      const token = await getAccessTokenSilently();
      await canjearCuponDescuento(Number(socioParsed.socio_id), 5, token);

      const puntosResp = await getPuntosBySocio(socioParsed.socio_id, token);
      setPuntosTotal(Number(puntosResp?.totalPuntos || 0));
      setHistorialPuntos(Array.isArray(puntosResp?.items) ? puntosResp.items.slice(0, 20) : []);

      const cuponesSocio = await obtenerCuponesPorSocio(socioParsed.socio_id, token);
      setMisCupones(Array.isArray(cuponesSocio) ? cuponesSocio : []);

      setCanjeMensaje("✅ Cupón 5% canjeado y asignado a tu socio.");
    } catch (e: any) {
      setCanjeMensaje(`❌ No se pudo canjear: ${e?.response?.data?.message || e?.message || "error"}`);
    } finally {
      setCanjeando(false);
    }
  };

  const abrirModalPuntos = () => {
    setPagePuntos(1);
    setShowPuntosModal(true);
    setLoadingHistorialModal(true);
    setTimeout(() => setLoadingHistorialModal(false), 600);
  };

  const pageSizePuntos = 5;
  const totalPagesPuntos = Math.max(1, Math.ceil(historialPuntos.length / pageSizePuntos));
  const historialPage = historialPuntos.slice((pagePuntos - 1) * pageSizePuntos, pagePuntos * pageSizePuntos);

  const quickActions: QuickAction[] = [
    {
      title: "Perfil",
      description: "Preferencias de tu cuenta y datos de facturación.",
      link: "/perfil",
      icon: User,
      accent: "from-sky-400 to-cyan-500",
    },
    {
      title: "Mi cuenta corriente",
      description: "Estado de cuotas, pagos pendientes y comprobantes.",
      link: "/cuotas",
      icon: CreditCard,
      accent: "from-blue-500 to-indigo-500",
    },
    {
      title: "Notificaciones",
      description: "Mensajes importantes y avisos de la Cámara.",
      link: "/notificaciones",
      icon: Bell,
      accent: "from-amber-400 to-orange-500",
    },
    {
      title: "Evento",
      description: "Charlas, rondas de negocios y capacitaciones disponibles.",
      link: "/eventos",
      icon: Calendar,
      accent: "from-purple-500 to-fuchsia-500",
    },
    {
      title: "Servicios",
      description: "Acceso directo a beneficios y convenios para socios.",
      link: "/servicios",
      icon: HandPlatter,
      accent: "from-emerald-400 to-teal-500",
    },
    {
      title: "Cupones",
      description: cuponesDescription,
      link: "/mis-cupones",
      icon: Ticket,
      accent: "from-rose-500 to-pink-500",
    },
  ];

  const nivel = getNivel(puntosTotal);
  const puntosParaSiguiente = nivel.next ? Math.max(0, nivel.next - puntosTotal) : 0;


  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10 space-y-10">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl">
          <div className="absolute inset-0 opacity-15">
            <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>
              <rect width="100%" height="100%" fill="url(#grad)" />
            </svg>
          </div>
          <div className="relative grid items-start gap-6 px-6 py-7 md:grid-cols-[1.1fr_0.9fr] md:px-10 lg:px-12">
            <div className="flex h-full flex-col gap-3">
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/25 bg-white/10 p-3 backdrop-blur">
                <div>
                  <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
                    {socio?.socio_nombre ?? "socio"}
                  </h1>
                  <p className="mt-1 text-sm font-semibold text-indigo-100">
                    ID Socio: {socio?.socio_id || "—"}
                  </p>
                  <p className="text-sm font-semibold text-indigo-100">
                    CUIT: {socio?.socio_cuit || "—"}
                  </p>
                  <p className="text-sm font-semibold text-indigo-100">
                    {socio?.socio_tipoEmpresa || "No informado"}
                  </p>
                </div>
                <div className="shrink-0 text-center">
                  {logoSocio && !logoError ? (
                    <img
                      src={logoSocio}
                      alt="Logo del socio"
                      className="mx-auto h-40 w-40 rounded-xl bg-white object-contain p-2 shadow"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-xl bg-white/20 text-white">
                      <User className="h-16 w-16" />
                    </div>
                  )}
                </div>
              </div>


              <div className="grid gap-2 sm:grid-cols-2">

                <InfoBadge label="Domicilio electrónico" value={domicilioElectronico} />
                <InfoBadge
                  label="Caja seguridad"
                  value={socio?.socio_tieneCajaSeguridad ? "Activa" : "No"}
                />
                <InfoBadge label="Celular" value={socio?.socio_celular || socio?.socio_telefono || "—"} />
                <InfoBadge label="Rubro" value={rubroNombre} />
              </div>
              <div className="mt-auto space-y-2 pt-2">
                <div className="flex flex-nowrap items-end gap-3 overflow-x-auto pb-1">
                  <button
                    onClick={() => navigate("/mis-comercios")}
                    className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    <Store className="h-4 w-4" />
                    Comercios
                  </button>
                  <button
                    onClick={() => navigate("/catalogo")}
                    className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Catálogo
                  </button>
                  <button
                    onClick={() => navigate("/camara-stream")}
                    className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    <PlaySquare className="h-4 w-4" />
                    Cámara Stream
                  </button>
                  <button
                    onClick={() => navigate("/mis-cajas")}
                    className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Caja de seguridad
                  </button>
                </div>
              </div>
            </div>

            <div className="grid gap-3">
              <div className="rounded-2xl border border-white/20 bg-white/90 p-4 text-slate-900 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md">
                    <Wallet className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-500">
                      Cuenta y deuda
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      Resumen actualizado
                    </p>
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-700">
                  {deudaTotal !== null ? (
                    deudaTotal > 0 ? (
                      <>
                        <p className="text-lg font-semibold text-slate-900">
                          Deuda total: ${deudaTotal.toLocaleString("es-AR")}
                        </p>
                        {socio?.socio_id && <DeudaInfo socio_id={socio.socio_id} compact />}
                      </>
                    ) : deudaTotal < 0 ? (
                      <p className="text-lg font-semibold text-emerald-700">
                        Saldo: -${Math.abs(deudaTotal).toLocaleString("es-AR")}
                      </p>
                    ) : (
                      <p className="text-lg font-semibold text-emerald-700">Sin deudas</p>
                    )
                  ) : socio?.socio_id ? (
                    <DeudaInfo socio_id={socio.socio_id} />
                  ) : (
                    <p className="text-slate-600">Seleccioná un socio para ver el estado de cuenta.</p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-white/20 bg-white/90 p-3 text-slate-900 shadow-lg">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white shadow-md">
                      <Sparkles className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase text-slate-500">
                        Puntos acumulados
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        Programa de beneficios
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={canjeando || puntosTotal < 50000}
                    onClick={handleCanjear5}
                    className="rounded-full bg-fuchsia-600 px-3 py-1 text-[11px] font-bold text-white transition hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:bg-fuchsia-300"
                  >
                    {canjeando ? "Canjeando..." : "Canjear 5%"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={abrirModalPuntos}
                  className="mt-2 w-full rounded-xl bg-slate-100/70 p-2.5 text-left transition hover:bg-slate-200/70"
                >
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Total actual</p>
                      {loading ? (
                        <div className="mt-1 h-7 w-20 animate-pulse rounded bg-slate-200" />
                      ) : (
                        <p className="mt-0.5 text-xl font-extrabold leading-none text-slate-900">{puntosTotal}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${nivel.nombre === "Oro" ? "bg-amber-100 text-amber-700" : nivel.nombre === "Plata" ? "bg-slate-200 text-slate-700" : "bg-orange-100 text-orange-700"}`}>
                      <span>{nivel.nombre === "Oro" ? "🥇" : nivel.nombre === "Plata" ? "🥈" : "🥉"}</span>
                      {nivel.nombre}
                    </span>
                  </div>
                  {nivel.next && (
                    <p className="mt-1 text-[11px] text-slate-600">Faltan <b>{puntosParaSiguiente}</b> para {nivel.next === 5000 ? "Oro" : "Plata"}.</p>
                  )}
                </button>
              </div>

            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Modo administrador
                </p>
                <p className="text-sm text-slate-700">
                  Buscá otro socio o gestioná cuentas.
                </p>
              </div>
              <button
                onClick={() => navigate("/admin")}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Panel de Control Admin
              </button>
            </div>
          </div>
        )}

        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-blue-600">Accesos rápidos</p>
              <h2 className="text-2xl font-bold text-slate-900">
                Todo lo importante en un vistazo
              </h2>
              <p className="text-sm text-slate-600">
                Ingresa directo a pagos, notificaciones, eventos y servicios.
              </p>
            </div>
            <a
              href="/servicios"
              onClick={(e) => {
                e.preventDefault();
                navigate("/servicios");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              Servicios disponibles
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <div
                  key={action.title}
                  onClick={() => navigate(action.link)}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-lg transition hover:-translate-y-1 hover:shadow-xl cursor-pointer"
                >
                  <div
                    className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${action.accent} text-white shadow-md`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {action.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {action.description}
                  </p>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-blue-600 transition group-hover:translate-x-1">
                    Ver detalles
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {showPuntosModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white p-4 shadow-2xl">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-600">Mis puntos</p>
                  <h3 className="text-xl font-bold text-slate-900">Histórico de movimientos</h3>
                </div>
                <button onClick={() => setShowPuntosModal(false)} className="rounded-lg p-2 hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-600" />
                </button>
              </div>

              {loadingHistorialModal ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
                  ))}
                </div>
              ) : historialPuntos.length === 0 ? (
                <p className="text-sm text-slate-500">Todavía no registrás movimientos de puntos.</p>
              ) : (
                <>
                  <div className="max-h-[60vh] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="py-2 pr-3">Comercio</th>
                          <th className="py-2 pr-3">Fecha</th>
                          <th className="py-2 pr-3">Puntos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historialPage.map((it) => (
                          <tr key={it.socio_puntos_id} className="border-b">
                            <td className="py-2 pr-3 font-medium text-slate-800">{it.comercio || "—"}</td>
                            <td className="py-2 pr-3 text-slate-600">{String(it.fecha_carga || "").slice(0, 19).replace("T", " ") || "—"}</td>
                            <td className={`py-2 pr-3 font-semibold ${Number(it.puntos || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {Number(it.puntos || 0) > 0 ? "+" : ""}{Number(it.puntos || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <button
                      type="button"
                      className="rounded border px-3 py-1 text-sm disabled:opacity-40"
                      disabled={pagePuntos <= 1}
                      onClick={() => setPagePuntos((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </button>
                    <span className="text-xs text-slate-600">Página {pagePuntos} de {totalPagesPuntos}</span>
                    <button
                      type="button"
                      className="rounded border px-3 py-1 text-sm disabled:opacity-40"
                      disabled={pagePuntos >= totalPagesPuntos}
                      onClick={() => setPagePuntos((p) => Math.min(totalPagesPuntos, p + 1))}
                    >
                      Siguiente
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
